import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/assets";
import { MaskLines } from "@/components/site/Reveal";
import { scrollToId } from "@/lib/scroll";

const scrollToWaitlist = () => scrollToId("#waitlist");

export const Hero = () => {
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-20, 40]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.1]);

  return (
    <section className="relative pt-32 md:pt-36 pb-16 overflow-hidden bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Intro text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.05 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="overline">CES 2027</span>
          <span className="h-px w-10 bg-white/25" />
          <span className="overline">By Quantum Origin</span>
        </motion.div>

        <h1 className="font-serif font-light leading-[0.9] tracking-[-0.02em] text-[15vw] sm:text-[12vw] lg:text-[7.4vw] max-w-5xl">
          <MaskLines
            lines={["Personalized", <em key="e" className="italic text-primary/90">skincare</em>, "delivered."]}
          />
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="max-w-md text-base md:text-lg text-muted-foreground leading-relaxed"
          >
            ASYA closes the loop between diagnosis, dose and skin response — an
            AI system that reads your skin and delivers the exact formulation it
            needs, every day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="flex flex-wrap items-center gap-5 shrink-0"
          >
            <button
              data-testid="hero-waitlist-button"
              onClick={scrollToWaitlist}
              className="group inline-flex items-center gap-3 rounded-full bg-accent px-8 py-4 text-accent-foreground text-sm font-medium hover:bg-white transition-all duration-300"
            >
              Get early access
              <span className="transition-transform duration-300 group-hover:translate-x-1.5">→</span>
            </button>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              $279 · Limited CES release
            </span>
          </motion.div>
        </div>

        {/* Product image, stacked under the text (mobile-friendly) */}
        <motion.div
          ref={frameRef}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-12 md:mt-16 rounded-2xl overflow-hidden border border-white/10"
        >
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] halo opacity-50" />
          <motion.img
            src={ASSETS.heroCenter}
            alt="ASYA VIBE applicator, DOSE cartridge and GAZE app"
            style={{ y: imgY, scale: imgScale }}
            className="w-full aspect-[3/2] object-cover object-center"
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 glass rounded-full px-4 py-2 border border-white/10">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]">VIBE · DOSE · GAZE</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
